/**
 * Página pública de informações sobre exclusão de conta.
 *
 * A exclusão em si é self-service DENTRO do app (Perfil → Meus dados → Excluir
 * conta), com período de carência de 30 dias e opção de cancelar — atendendo à
 * Apple App Store Guideline 5.1.1(v). Esta página NÃO é mais o passo que conclui
 * a exclusão; ela existe para:
 *  1. Google Play User Data policy — URL pública linkada na ficha da Play Console
 *     que permite ao usuário pedir exclusão sem precisar do app.
 *  2. Explicar o que acontece com os dados (retenção legal) e oferecer um canal
 *     por e-mail como ALTERNATIVA (nunca exigência) para quem não tem o app.
 *
 * Também cobre a solicitação de **exportação de dados** (LGPD Art. 18) através do
 * mesmo canal humano (privacidade@meiucaapp.com.br).
 *
 * Migrada de `meiuca-site` (Next.js) para `meiuca-app-web-react` (React Router).
 */

import { Body, H2, H3 } from '@meiuca/ui-components'
import { LegalPageLayout } from '../components/LegalPageLayout'

export function ExcluirContaPage() {
  return (
    <LegalPageLayout
      title="Excluir minha conta"
      documentTitle="Excluir conta — Meiuca"
      lastUpdated="30 de junho de 2026"
    >
      <Body>
        Você tem o direito de solicitar a exclusão da sua conta Meiuca a qualquer momento.
        Esta página explica como fazer isso e o que acontece com seus dados depois.
      </Body>

      <section>
        <H2 className="text-xl md:text-2xl mt-8 mb-3">Como excluir</H2>
        <H3 className="text-lg mt-4 mb-2">Pelo aplicativo (recomendado)</H3>
        <Body>
          Abra o app Meiuca, acesse <strong>Perfil → Meus dados</strong> e toque em
          {' '}<strong>Excluir conta</strong>. A tela explica o que acontece e você confirma
          a exclusão ali mesmo — todo o processo acontece dentro do app, sem precisar de mais
          nenhum passo. Sua conta entra em um período de <strong>30 dias</strong>, e você pode
          {' '}<strong>cancelar a exclusão</strong> pelo próprio app a qualquer momento dentro
          desse prazo.
        </Body>

        <H3 className="text-lg mt-6 mb-2">Por e-mail (alternativa)</H3>
        <Body>
          Se você não tem mais acesso ao app, também pode pedir a exclusão por e-mail para{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br?subject=Solicita%C3%A7%C3%A3o%20de%20exclus%C3%A3o%20de%20conta"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>{' '}
          a partir do e-mail que você usa em sua conta (ou informe o telefone cadastrado),
          com o assunto <em>&ldquo;Solicitação de exclusão de conta&rdquo;</em>. Pode ser
          que entremos em contato para confirmar sua identidade antes de processar a exclusão.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">O que acontece com seus dados</H2>
        <Body>
          Ao confirmar a exclusão, sua conta entra em um período de carência de{' '}
          <strong>30 dias</strong>, durante o qual você pode cancelar e recuperar o acesso.
          Passado esse prazo, removemos ou anonimizamos seus dados pessoais
          (nome, telefone, nickname, senha). Algumas informações precisam ser retidas por
          obrigação legal — por exemplo, registros de pagamentos e reservas concluídas,
          mantidos por <strong>5 anos</strong> para fins fiscais e contratuais
          (Lei 13.709/2018, art. 16). Esses registros ficam armazenados em forma desvinculada
          da sua identidade quando possível.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">Exportar uma cópia dos seus dados</H2>
        <Body>
          A LGPD garante seu direito de receber uma cópia legível dos dados pessoais
          que armazenamos sobre você. Para solicitar a exportação, escreva para{' '}
          <a
            href="mailto:privacidade@meiucaapp.com.br?subject=Solicita%C3%A7%C3%A3o%20de%20exporta%C3%A7%C3%A3o%20de%20dados"
            className="text-brand-primary hover:underline"
          >
            privacidade@meiucaapp.com.br
          </a>{' '}
          com o assunto <em>&ldquo;Solicitação de exportação de dados&rdquo;</em>.
          Costumamos atender esse pedido em até 15 dias úteis.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">Outros direitos como titular</H2>
        <Body>
          Você também pode pedir confirmação dos dados que armazenamos, correção de
          informações incompletas ou desatualizadas, anonimização, portabilidade e
          revogação de consentimento — todos pelo mesmo canal de contato. Em caso
          de dúvida sobre o tratamento dos seus dados, consulte nossa{' '}
          <a href="/privacidade" className="text-brand-primary hover:underline">
            Política de Privacidade
          </a>{' '}
          ou registre uma reclamação na{' '}
          <a
            href="https://www.gov.br/anpd/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline"
          >
            Autoridade Nacional de Proteção de Dados (ANPD)
          </a>.
        </Body>
      </section>
    </LegalPageLayout>
  )
}
